import {Element, Component, h, Host, Method, Prop} from '@stencil/core';

import type DataFlux from '../../../../utils/libs/dataflux/dataflux';
import {GuidedDataFlux} from '../../flux';
import {authenticationHeaders} from '../../../guided/subcomponents/utils';
import {composeFunctions2, composeFunctions3} from '../../../../utils/libs/middleware';
import {makeLogger} from '../../../../logger/logger';
import type {GuidedRequest} from '../../models/cose';
import type {GuidedMiddleware, GuidedMiddlewareRegistry} from '../../models/middleware';
import {extractTealiumId, getCookieValue} from '../../../../utils/helpers/cookie-manager';


const {
  // info, debug,
  warn,
  err
} = makeLogger({
  pathToCurrentFile: 'components/guided2/subcomponents/backends/stacked.tsx'
});


const fallbackNoop = async (_request: GuidedRequest, _dataflux: DataFlux<GuidedDataFlux>) => {
    return [];
};

type CompoundCallArg = Parameters<GuidedMiddleware>[0];
type CompoundCallReturn = ReturnType<GuidedMiddleware>;

@Component({
  tag: 'uiaas-guided-search-v2-backend-stack',
  shadow: false
})
export class GuidedV2Backend{

   @Element() el!: HTMLUiaasGuidedSearchV2BackendStackElement;

   /**
   * GuidedV2 Middleware stack, pipe separated list of middleware names to be called
   */
    @Prop() readonly stack: string;
    /**
   * Api endpoint for Cose V2 suggestions
   */

  /**
   * Custom headers to add
   */
  @Prop() readonly customHeaders: string = '{}';
  /**
   * cose context object, as string
   */
    @Prop() readonly context: string = '{}';
  /**
   * Searcher to be used
   */
  @Prop() readonly searcher: string;

  /**
   * Api endpoint
   */
  @Prop() readonly api: string;
  /**
   * Api key, if applicable
   */
    @Prop() readonly apikey?: string;
  /**
   * Bearer token, if applicable
   */
  @Prop() readonly bearer?: string;
  /**
   * Token url for getting the bearer token
   */
    @Prop() readonly tokenUrl?: string;
  /**
   * IDMS token, if applicable
   */
  @Prop() readonly idmsToken?: string;
  /**
   * Basic authentication, if applicable
   */
    @Prop() readonly apigeeBasicAuth?: string;

  /**
   * Timeout for backend to respond, in milliseconds
   */
  @Prop() readonly timeoutMs: number = 10_000;

  /**
   * The JSON string for groupOptions request parameter for Cose
   */
  @Prop() readonly groupOptions: string = '[]';

  /**
   * Custom fields for request body can be put here, represented as json string
   */
  @Prop() readonly customRequestBody: string = '{}';

  /**
   * Tells how many items Cose should return in single group
   */
  @Prop() readonly limitPerGroup: number = 5;

  private getGroupOptions(){
      try{
        return JSON.parse(this.groupOptions);
      }catch(ignored){
        return {};
      }
  }
  private getCustomRequestBody(){
      try{
        return JSON.parse(this.groupOptions);
      }catch(ignored){
        return {};
      }
  }

  private getCoseContext(){
      try{
        return JSON.parse(this.context);
      }catch(e){
        err('Failed to parse Cose Context', e);
        throw new Error('Failed to parse Cose Context');
      }
  }



  private getCustomHeaders(){
      try{
        return JSON.parse(this.customHeaders);
      }catch(e){
        err('Failed to parse Custom Headers', e);
        return {};
      }
  }

  // private getTealium(){
  //     const tealAttribute = this.el.getAttribute('tealium');
  //     if(tealAttribute){
  //       return {'X-TEAL-User': tealAttribute};
  //     }
  //     const tealCookie = extractTealiumId(getCookieValue('TEAL'));
  //     if(tealCookie) {
  //       return {'X-TEAL-User': tealCookie};
  //     }
  //     return {};
  // }


  /**
   * Fetches the data from the backend
   * @param dataflux DataFlux<GuidedDataFlux>
   * @param middlewareRegistry GuidedMiddlewareRegistry
   * @returns Promise<GuidedItemsV2>
   */
    @Method() async fetch (
    dataflux: DataFlux<GuidedDataFlux>,
    middlewareRegistry: GuidedMiddlewareRegistry,
  ): CompoundCallReturn {

      // console.log("Triggered backend.fetch call", this.stack);

      const stack = await Promise.all(
        this.stack
          .split('|')
          .map(async middlewareName => {
              if(!middlewareRegistry[middlewareName]) {
                // This happens on 1st load, when used middleware is to be registered
                // by the hosting application. Like in storybook, or mySchneider

                warn('Can\'t find middleware', {middlewareName},'using fallback instead; used registry:',{middlewareRegistry});
                return fallbackNoop;
              }
              return await middlewareRegistry[middlewareName]();
          })
      );
      //console.log("Collected middleware stack:", stack);


      const api: (params: CompoundCallArg) => CompoundCallReturn = composeFunctions3(...stack);



      const request: GuidedRequest = {
        searchTerm: dataflux.root.searchTerm,
        apiEndpoint: this.api,
        timeoutMs: this.timeoutMs,
        searcher: this.searcher,
        groupOptions: this.getGroupOptions(),
        limitPerGroup: this.limitPerGroup,
        customRequestBody: this.getCustomRequestBody(),
        context: this.getCoseContext(),
        customHeaders: this.getCustomHeaders(),
        auth: await authenticationHeaders({
          apikey: this.apikey,
          bearer: this.bearer,
          tokenUrl: this.tokenUrl,
          idmsToken: this.idmsToken,
          apigeeBasicAuth: this.apigeeBasicAuth,
          // ...this.getTealium(),
        }),
      };
      try{
        return await api({
          guidedRequest: request,
          dataflux,
          backendElement: this.el,
        });
      }catch(e){
        err('Failed to call backend', e);
        return [];
      }
  }

    render(){
        return <Host />;
    }
}
